export type FitProfile = {
  id: "default";
  name: string;
  buildX: number;
  buildY: number;
  buildZ: number;
  nozzle: number;
  slidingClearance: number;
  updatedAt: string;
};

const DB_NAME = "partharbor-tools";
const FIT_PROFILE_STORE = "fit-profiles";
const RECIPE_STORE = "recipes";
const DB_VERSION = 2;

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(FIT_PROFILE_STORE)) request.result.createObjectStore(FIT_PROFILE_STORE, { keyPath: "id" });
      if (!request.result.objectStoreNames.contains(RECIPE_STORE)) request.result.createObjectStore(RECIPE_STORE, { keyPath: "id" });
    };
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function loadFitProfile(): Promise<FitProfile | undefined> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(FIT_PROFILE_STORE, "readonly");
    const request = transaction.objectStore(FIT_PROFILE_STORE).get("default");
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result as FitProfile | undefined);
    transaction.oncomplete = () => database.close();
  });
}

export async function saveFitProfile(profile: FitProfile): Promise<void> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(FIT_PROFILE_STORE, "readwrite");
    transaction.objectStore(FIT_PROFILE_STORE).put(profile);
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => { database.close(); resolve(); };
  });
}

export async function listRecipes(): Promise<RecipeRecord[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(RECIPE_STORE, "readonly");
    const request = transaction.objectStore(RECIPE_STORE).getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve((request.result as RecipeRecord[]).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
    transaction.oncomplete = () => database.close();
  });
}

export async function saveRecipe(recipe: RecipeRecord): Promise<void> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(RECIPE_STORE, "readwrite");
    transaction.objectStore(RECIPE_STORE).put(recipe);
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => { database.close(); resolve(); };
  });
}

export async function deleteRecipe(id: string): Promise<void> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(RECIPE_STORE, "readwrite");
    transaction.objectStore(RECIPE_STORE).delete(id);
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => { database.close(); resolve(); };
  });
}

export async function clearToolMetadata(): Promise<void> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([FIT_PROFILE_STORE, RECIPE_STORE], "readwrite");
    transaction.objectStore(FIT_PROFILE_STORE).clear();
    transaction.objectStore(RECIPE_STORE).clear();
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => { database.close(); resolve(); };
  });
}
import type { RecipeRecord } from "@partharbor/workbench-contracts";
